import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useMemo, useRef } from "react";

import { EmptyHint } from "./extensions/EmptyHint";
import { PlaceholderNode } from "./nodes/PlaceholderNode";
import { htmlToTemplate, templateToHtml } from "./RichTemplateEditor.utils";

interface UseRichEditorArgs {
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  id?: string;
  disabled?: boolean;
}

/**
 * Инстанцирует TipTap-редактор один раз и держит value-sync с RHF-полем.
 *
 * Зачем хук: 1) изолирует TipTap-специфичную обвязку (refs для stale-closure,
 * мемоизация extensions/props, value-sync через `setContent`), 2) даёт
 * родителю чистый интерфейс «дай value/onChange — получи `Editor`».
 */
export const useRichEditor = ({
  value,
  onChange,
  onBlur,
  placeholder,
  ariaInvalid,
  ariaDescribedBy,
  id,
  disabled,
}: UseRichEditorArgs) => {
  // onChange/onBlur замыкаются в onUpdate/onBlur ОДНИМ разом (deps=[]),
  // поэтому держим ref'ы — иначе родитель получал бы вызовы со stale
  // closure (старый onChange → stale RHF setter).
  const lastEmittedRef = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  // Avito allowlist: p, br, strong, em, ul, ol, li. Остальное в StarterKit
  // отключаем — ProseMirror schema не примет, плюс DOMPurify в templateToHtml
  // фильтрует входной HTML на уровне строки.
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        link: false,
        underline: false,
        dropcursor: false,
        gapcursor: false,
        listKeymap: false,
        trailingNode: false,
      }),
      PlaceholderNode,
      EmptyHint.configure({ text: placeholder ?? "" }),
    ],
    [placeholder],
  );

  // editorProps стабилен по референсу: TipTap сравнивает options референсно
  // (`@tiptap/react` `compareOptions`), и если мы каждый раз даём новый
  // объект — `editor.setOptions` дёргается на keystroke с view.updateState.
  const editorProps = useMemo(
    () => ({
      attributes: {
        ...(id ? { id } : {}),
        role: "textbox",
        "aria-multiline": "true",
        ...(ariaInvalid ? { "aria-invalid": "true" } : {}),
        ...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {}),
        spellcheck: "false",
      },
    }),
    [id, ariaInvalid, ariaDescribedBy],
  );

  // Initial content считаем один раз; правки value идут через useEffect-sync
  // ниже. Если положить `templateToHtml(value)` в options — full O(N) regex +
  // DOM-walk бежит на каждом рендере, даже когда value не менялась.
  const initialContentRef = useRef<string>("");
  if (!initialContentRef.current && value) {
    initialContentRef.current = templateToHtml(value);
  }

  const editor = useEditor(
    {
      extensions,
      editable: !disabled,
      content: initialContentRef.current,
      onUpdate({ editor: ed }) {
        // editor.isEmpty покрывает все варианты «пустого» документа по
        // schema (например `<ul><li><p></p></li></ul>` после backspace) —
        // иначе hasTemplate-флаг в preview-панели остаётся true.
        const next = ed.isEmpty ? "" : htmlToTemplate(ed.getHTML());
        lastEmittedRef.current = next;
        onChangeRef.current(next);
      },
      onBlur() {
        onBlurRef.current?.();
      },
      editorProps,
    },
    [],
  );

  // Внешние правки value (applySuggestion в UnknownVariableList, reset формы) —
  // синхронизируем только когда value реально разошёлся с последним эмитом,
  // иначе setContent дёргался бы на каждый keystroke и сбивал каретку.
  useEffect(() => {
    if (!editor) return;
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- синхронизация controlled value в императивный TipTap-инстанс (внешняя система)
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    editor.commands.setContent(templateToHtml(value), { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  return editor;
};
